import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import API from "../api";

interface LabInfo {
  lab_name: string;
  location: string;
  contact_no: string;
  email?: string;
  show_receipt_header?: number;
  receipt_header?: string;
}

interface PatientInfo {
  P_name: string;
  prefix: string;
  P_code: string;
  mobile: string;
  gender?: string;
  age?: number;
  age_type?: string;
  address?: string;
  email?: string;
}

interface BillData {
  id: number;
  billnum: string;
  bill_date: string;
  total: number;
  net_amount: number;
  cgst?: number;
  sgst?: number;
  home_visit?: number;
  discount?: number;
  paid_amount: number;
  balance: number;
  payment_method: string;
  reference_no?: string;
  ref_prefix: string;
  reference: string;
  bill_status: number;
  created_by?: string;
}

interface TestItem {
  item_name: string;
  amount: number;
  unit_price: number;
  quantity: number;
  uom: string;
}

interface DisplayCategory {
  name: string;
  amount: number;
}

interface A4ReceiptData {
  labInfo: LabInfo;
  patientInfo: PatientInfo;
  billData: BillData;
  testItems: TestItem[];
  singleDisplay: DisplayCategory[];
  groupDisplay: DisplayCategory[];
}

// Format a date/timestamp as DD/MM/YYYY in Indian Standard Time (IST).
// "en-GB" reliably renders DD/MM/YYYY with "/" separators; the explicit
// timeZone locks it to IST regardless of the browser/server's local zone.
const formatDateIST = (value?: string | number | Date | null): string => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata" });
};

const BillPdf: React.FC = () => {
  const { billId } = useParams<{ billId: string }>();
  const navigate = useNavigate();

  const [data, setData] = useState<A4ReceiptData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (billId) {
      fetchReceipt(billId);
    } else {
      setError("No bill ID provided in URL");
      setLoading(false);
    }
  }, [billId]);

  const fetchReceipt = async (id: string) => {
    try {
      const res = await API.get(`/patients/receipt/a4/${id}`);
      if (res.data?.success) {
        setData(res.data.data);
      } else {
        setError("Receipt not found");
      }
    } catch (err) {
      console.error("BillPdf fetchReceipt error:", err);
      setError("Failed to load receipt");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 60, fontFamily: "sans-serif" }}>
        <div className="spinner-border text-primary" />
        <p style={{ marginTop: 12 }}>Loading receipt…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div style={{ textAlign: "center", padding: 60, color: "red", fontFamily: "sans-serif" }}>
        <h3>⚠ Error</h3>
        <p>{error || "Receipt not found"}</p>
      </div>
    );
  }

  const { labInfo, patientInfo, billData, testItems, singleDisplay, groupDisplay } = data;

  const allItems: { name: string; qty: number; rate: number; amount: number }[] = [
    // testItems now contains both 'test' and 'single' items (backend merges them)
    ...testItems.map((t) => ({
      name: t.item_name,
      qty: Number(t.quantity) || 1,
      rate: Number(t.unit_price) || 0,
      amount: Number(t.amount) || 0,
    })),
    ...groupDisplay.map((g) => ({
      name: g.name,
      qty: 1,
      rate: Number(g.amount) || 0,
      amount: Number(g.amount) || 0,
    })),
  ];

  const subTotal = allItems.reduce((s, i) => s + i.amount, 0);
  const netTotal = Number(billData.net_amount || 0);
  const paid     = Number(billData.paid_amount || 0);
  const balance  = Number(billData.balance ?? netTotal - paid);
  const cgst     = Number(billData.cgst || 0);
  const sgst     = Number(billData.sgst || 0);

  return (
    <>
      <style>{`
        @page { size: A4; margin: 12mm; }
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; padding: 0 !important; margin: 0 !important; }
          .page-wrap { display: block !important; min-height: auto !important; padding: 0 !important; margin: 0 !important; background: white !important; }
          .a4 { width: 100% !important; min-height: auto !important; padding: 0 !important; box-shadow: none !important; margin: 0 !important; }
        }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body {
          background: #efefef;
          padding: 20px;
          font-family: Arial, Helvetica, sans-serif;
          color: #111;
          font-size: 12px;
        }
        .page-wrap {
          display: flex;
          justify-content: center;
          min-height: 100vh;
        }
        .a4 {
          width: 210mm;
          min-height: 297mm;
          background: #fff;
          padding: 18mm 12mm 10mm;
          box-shadow: 0 2px 12px rgba(0,0,0,0.08);
        }
        .receipt-header {
          text-align: center;
          margin-bottom: 10px;
        }
        .receipt-title {
          font-size: 23px;
          font-weight: 800;
          letter-spacing: 0.5px;
          text-transform: uppercase;
          color: #000;
        }
        .receipt-sub {
          font-size: 12px;
          color: #333;
          margin-top: 2px;
        }
        .receipt-sub strong {
          font-weight: 700;
        }
        .header-separator {
          border-top: 1px solid #111;
          border-bottom: 1px solid #111;
          margin: 8px 0 10px;
          height: 2px;
        }
        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          column-gap: 18px;
          row-gap: 8px;
          margin-bottom: 10px;
          font-size: 12px;
        }
        .meta-row {
          display: flex;
          align-items: center;
          gap: 8px;
          min-height: 22px;
          line-height: 1.2;
        }
        .meta-row .label {
          font-weight: 700;
          min-width: 88px;
          color: #222;
        }
        .meta-row .value {
          color: #111;
          font-weight: 500;
        }
        .bill-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 8px;
          font-size: 12px;
        }
        .bill-table th {
          border-bottom: 2px solid #111;
          padding: 7px 6px;
          font-weight: 700;
          text-align: left;
          color: #111;
        }
        .bill-table th:nth-child(2), .bill-table td:nth-child(2) { width: 70%; }
        .bill-table th:nth-child(3), .bill-table td:nth-child(3) { width: 30%; text-align: right; }
        .bill-table td {
          padding: 6px 6px;
          border-bottom: 1px solid #d9d9d9;
          vertical-align: top;
        }
        .bill-table tbody tr:last-child td { border-bottom: none; }
        .totals-wrap {
          display: flex;
          justify-content: flex-end;
          margin-top: 10px;
          margin-bottom: 16px;
        }
        .totals-box {
          width: 230px;
          font-size: 12px;
        }
        .totals-row {
          display: flex;
          justify-content: space-between;
          padding: 3px 0;
          border-bottom: 1px solid #ddd;
        }
        .totals-row:last-child { border-bottom: none; }
        .totals-row.highlight {
          font-weight: 700;
          padding-top: 6px;
          border-top: 1px solid #111;
          margin-top: 4px;
        }
        .amount-text {
          border-top: 1px solid #444;
          padding-top: 8px;
          font-size: 12px;
          margin-top: 6px;
          color: #222;
        }
        .amount-text strong { font-weight: 700; }
        .footer-sign {
          text-align: right;
          margin-top: 18px;
          padding-top: 8px;
          border-top: 1px solid #111;
          font-size: 12px;
          font-weight: 700;
          color: #111;
        }
        .action-bar {
          display: flex;
          justify-content: center;
          gap: 12px;
          margin-top: 20px;
        }
        .btn-print {
          padding: 8px 28px;
          background: #2d2d2d;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 13px;
        }
        .btn-back {
          padding: 8px 28px;
          background: #7d7d7d;
          color: white;
          border: none;
          border-radius: 4px;
          cursor: pointer;
          font-size: 13px;
        }
      `}</style>

      <div className="page-wrap">
        <div>
          <div className="a4">
            <div className="receipt-header">
              <div className="receipt-title">{labInfo.lab_name || "Healthcare Diagnostics Centre"}</div>
              <div className="receipt-sub">{labInfo.location || ""}</div>
              <div className="receipt-sub">Ph: {labInfo.contact_no || ""}</div>
            </div>

            <div className="header-separator" />

            <div className="info-grid">
              <div className="meta-row">
                <span className="label">Bill No:</span>
                <span className="value">{billData.billnum || "-"}</span>
              </div>
              <div className="meta-row">
                <span className="label">Date:</span>
                <span className="value">{formatDateIST(billData.bill_date)}</span>
              </div>

              <div className="meta-row">
                <span className="label">Patient:</span>
                <span className="value">{patientInfo.prefix}. {patientInfo.P_name}</span>
              </div>
              <div className="meta-row">
                <span className="label">Patient ID:</span>
                <span className="value">{patientInfo.P_code}</span>
              </div>

              <div className="meta-row">
                <span className="label">Mobile:</span>
                <span className="value">{patientInfo.mobile || "-"}</span>
              </div>
              <div className="meta-row">
                <span className="label">Payment:</span>
                <span className="value">{billData.payment_method || "-"}</span>
              </div>

              <div className="meta-row">
                <span className="label">Reference:</span>
                <span className="value">{billData.ref_prefix ? `${billData.ref_prefix} ${billData.reference || ""}` : billData.reference || "-"}</span>
              </div>
            </div>

            <table className="bill-table">
              <thead>
                <tr>
                  <th style={{ width: 38 }}>S No</th>
                  <th>Description</th>
                  <th>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {allItems.length === 0 ? (
                  <tr>
                    <td colSpan={3} style={{ textAlign: "center", color: "#666", padding: 16 }}>No items found</td>
                  </tr>
                ) : (
                  allItems.map((item, idx) => (
                    <tr key={idx}>
                      <td>{idx + 1}</td>
                      <td>{item.name}</td>
                      <td style={{ textAlign: "right" }}>{item.amount.toFixed(2)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <div className="totals-wrap">
              <div className="totals-box">
                <div className="totals-row">
                  <span>Sub-Total:</span>
                  <span>₹{subTotal.toFixed(2)}</span>
                </div>
                <div className="totals-row">
                  <span>Net Amount:</span>
                  <span>₹{netTotal.toFixed(2)}</span>
                </div>
                <div className="totals-row">
                  <span>Paid Amount:</span>
                  <span>₹{paid.toFixed(2)}</span>
                </div>
                <div className="totals-row highlight">
                  <span>Balance:</span>
                  <span>₹{balance.toFixed(2)}</span>
                </div>
              </div>
            </div>

            <div className="amount-text">
              <strong>Amount in words:</strong> INR {subTotal.toFixed(2)} Rupees Only
            </div>

            <div className="footer-sign">For {labInfo.lab_name || "HEALTHCARE DIAGNOSTICS CENTRE"}</div>
          </div>

          <div className="action-bar no-print">
            <button className="btn-print" onClick={() => window.print()}>Print / Save PDF</button>
            <button className="btn-back" onClick={() => navigate(-1)}>Back</button>
          </div>
        </div>
      </div>
    </>
  );
};

export default BillPdf;